import { Badge, TooltipRenderer, Box, Text, Stack, Button } from '@machobear/design-system'
import { useTranslation } from 'react-i18next'
import config from '@/config'
import useLocalStorage from '@/hooks/use-localstorage'
import { Modals } from '../types'
const { APP_PATH } = config

type StatusProps = {
  status: keyof typeof trustScoreStatuses
  id: String
  isBillingUserValid?: boolean
  showModal: (modal: keyof Modals) => () => void
}

const trustScoreStatuses = {
  completed: {
    badgeTone: 'positive',
    badgeText: 'Success',
    buttonText: 'View',
    buttonFunction: undefined,
    href: `${APP_PATH}/trust-score/view/`,
    disabled: false
  },
  draft: {
    badgeTone: 'info',
    badgeText: 'Draft',
    buttonText: 'Continue',
    buttonFunction: undefined,
    href: `${APP_PATH}/trust-score/edit/%s/credit-check`,
    disabled: true
  },
  pending: {
    badgeTone: 'caution',
    badgeText: 'Pending',
    buttonText: 'View',
    buttonFunction: undefined,
    href: `${APP_PATH}/trust-score/view/`,
    disabled: true
  },
  failed: {
    badgeTone: 'critical',
    badgeText: 'Failed',
    buttonText: 'View',
    buttonFunction: undefined,
    href: `${APP_PATH}/trust-score/view/`,
    disabled: false
  },
  expired: {
    badgeTone: 'neutral',
    badgeText: 'Expired',
    buttonText: 'Continue',
    buttonFunction: undefined,
    href: `${APP_PATH}/trust-score/edit/%s/credit-check`,
    disabled: true
  }
}

export const RenderBadge = ({ status }: Omit<StatusProps, 'id'>) => {
  const { t } = useTranslation()
  return (
    <Badge tone={trustScoreStatuses[status].badgeTone}>
      {t(`l.standalone_trustscore.list.badge.${status}`, trustScoreStatuses[status].badgeText)}
    </Badge>
  )
}

export const RenderStatusButton = ({ status, id, showModal, isBillingUserValid }: StatusProps) => {
  const { t } = useTranslation()
  const [leftFormType = 'credit-check'] = useLocalStorage(`standalone-trustscore-${id}`)

  return (
    <>
      {status === 'expired' ? (
        <TooltipRenderer
          id={`tooltip-expired-${id}`}
          tooltip={
            <Stack space="xsmall">
              <Text size="standard" weight="strong">
                {t('l.standalone_trustscore.list.expired.text', 'Expired')}
              </Text>
              <Text size="small">
                {t(
                  'l.standalone_trustscore.list.expired.description',
                  'This has expired due to inactivity after 3 months. Please submit a new Trust Score and try again.'
                )}
              </Text>
            </Stack>
          }
        >
          {({ triggerProps }) => (
            <Box {...triggerProps}>
              <Button variant="ghost" size="small" iconPosition="leading" disabled={true}>
                {trustScoreStatuses[status].buttonText}
              </Button>
            </Box>
          )}
        </TooltipRenderer>
      ) : status === 'pending' ? (
        <Button
          onClick={e => {
            e.stopPropagation()
          }}
          variant="ghost"
          size="small"
          iconPosition="leading"
          disabled={trustScoreStatuses[status].disabled}
        >
          {trustScoreStatuses[status].buttonText}
        </Button>
      ) : (
        <Button
          onClick={e => {
            e.stopPropagation()
            const url =
              status === 'draft'
                ? `${APP_PATH}/trust-score/edit/${id}/${leftFormType}`
                : `${trustScoreStatuses[status].href}${id}`

            isBillingUserValid ? window.open(url, '_blank') : showModal('billingUser')()
          }}
          variant="ghost"
          size="small"
          iconPosition="leading"
        >
          {t(
            `l.standalone_trustscore.list.${status}.button_text`,
            trustScoreStatuses[status].buttonText
          )}
        </Button>
      )}
    </>
  )
}
